import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Indicator,
  Menu,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  FiBell,
  FiChevronDown,
  FiChevronLeft,
  FiHome,
  FiLogOut,
  FiMenu,
  FiUser,
} from "react-icons/fi";
import { logout } from "../../features/auth/authActions";
import { resetStep, setFormData } from "../../features/formData/formSlice";
import {
  ADMIN_PANEL_HOME,
  getAdminPageTitle,
  getUserDisplayName,
  getUserTypeName,
} from "./adminNavigation";

const AdminTopbar = ({
  collapsed = false,
  isMobile = false,
  onOpenDrawer,
  onToggleCollapse,
}) => {
  const authState = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pageTitle = getAdminPageTitle(location.pathname);
  const displayName = getUserDisplayName(authState);
  const userType = getUserTypeName(authState) ?? "ADMIN";
  const initials = useMemo(
    () =>
      displayName
        .split(" ")
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(""),
    [displayName]
  );

  const handleLogout = () => {
    dispatch(resetStep());
    dispatch(setFormData({}));
    dispatch(logout());
    navigate("/admin-login", { replace: true });
  };

  return (
    <header className="admin-topbar">
      <div className="admin-topbar__inner">
        <div className="admin-topbar__main">
          <button
            type="button"
            className="admin-topbar__shell-button"
            onClick={isMobile ? onOpenDrawer : onToggleCollapse}
            aria-label={
              isMobile
                ? "Open navigation"
                : collapsed
                ? "Expand navigation"
                : "Collapse navigation"
            }
          >
            {isMobile ? (
              <FiMenu size={18} />
            ) : (
              <FiChevronLeft
                size={18}
                style={{
                  transform: collapsed ? "rotate(180deg)" : "none",
                  transition: "transform 0.18s ease",
                }}
              />
            )}
          </button>

          <div className="admin-topbar__copy">
            <h1 className="admin-topbar__title">{pageTitle}</h1>
          </div>
        </div>

        <div className="admin-topbar__actions">
          <Link to="/notification" className="admin-topbar__icon-link">
            <Indicator
              inline
              size={10}
              color="red"
              disabled={location.pathname === "/notification"}
              offset={3}
            >
              <FiBell size={18} />
            </Indicator>
          </Link>

          <Menu shadow="md" width={220} position="bottom-end" withArrow>
            <Menu.Target>
              <UnstyledButton className="admin-topbar__profile">
                <Avatar radius="xl" size={38} color="blue">
                  {initials || "AD"}
                </Avatar>

                <div className="admin-topbar__profile-copy">
                  <Text className="admin-topbar__profile-name">{displayName}</Text>
                  <Text className="admin-topbar__profile-role">
                    {userType.replaceAll("_", " ")}
                  </Text>
                </div>

                <FiChevronDown size={16} className="admin-topbar__profile-caret" />
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>Quick Access</Menu.Label>
              <Menu.Item
                leftSection={<FiHome size={15} />}
                component={Link}
                to={ADMIN_PANEL_HOME}
              >
                Dashboard
              </Menu.Item>
              <Menu.Item
                leftSection={<FiUser size={15} />}
                component={Link}
                to="/user/profile"
              >
                Profile
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<FiLogOut size={15} />}
                onClick={handleLogout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
    </header>
  );
};

export default AdminTopbar;
